import { HttpClient } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { Subscription } from 'rxjs';

import { Constant } from 'src/app/constant/constant';

@Component({
  selector: 'calsoft-common-images',
  templateUrl: './common-images.component.html',
  styleUrls: ['./common-images.component.scss']
})
export class CommonImagesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() src: string;
  @Input() alt;
  @Input() onerror;
  @Input() width = '50px';
  @Input() height = '50px';
  cid: string;
  display: boolean = true;
  safeUrl: SafeUrl | string = '/assets/images/logo/no-image.png';
  fileType: string = 'image';
  private currentObjectUrl: string | null = null;
  private subscription: Subscription;


  constructor(private http: HttpClient, private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this.loadImage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['src']) {
      this.loadImage();
    }
  }

  getEnterpriseToken(): string {
    // 1. Check localStorage
    try {
      const ls = localStorage.getItem(Constant.enterPriseKey) || localStorage.getItem('cid') || localStorage.getItem('c-id');
      if (ls && ls.trim() !== '' && ls !== 'null' && ls !== 'undefined') {
        return ls.trim();
      }
    } catch (e) {}

    // 2. Check document.cookie (standard in web mode)
    try {
      if (typeof document !== 'undefined' && document.cookie) {
        const cookies = document.cookie.split(';');
        const targetKey = Constant.enterPriseKey || 'c-id';
        for (let c of cookies) {
          const trimmed = c.trim();
          if (trimmed.startsWith(targetKey + '=')) {
            const val = trimmed.substring(targetKey.length + 1).trim();
            if (val && val !== 'null' && val !== 'undefined') {
              return decodeURIComponent(val);
            }
          }
        }
      }
    } catch (e) {}

    // 3. Check sessionStorage
    try {
      const ss = sessionStorage.getItem(Constant.enterPriseKey) || sessionStorage.getItem('cid') || sessionStorage.getItem('c-id');
      if (ss && ss.trim() !== '' && ss !== 'null' && ss !== 'undefined') {
        return ss.trim();
      }
    } catch (e) {}

    return '';
  }

  getFullUrl(src: string): string {
    if (!src) return '';
    const token = this.getEnterpriseToken();
    if (!token) return src;

    // Check if cid is already in src
    if (src.includes('cid=')) {
      return src;
    }

    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}cid=${encodeURIComponent(token)}`;
  }

  isLocalOrDataUrl(url: string): boolean {
    if (!url) return false;
    return url.startsWith('assets/') ||
           url.startsWith('/assets/') ||
           url.startsWith('data:') ||
           url.startsWith('blob:');
  }

  getFileNameFromUrl(src: string): string {
    if (!src) return '';
    let filename = '';

    // Check if there is a query string
    if (src.includes('?')) {
      const queryString = src.split('?')[1];
      const params = queryString.split('&');
      for (const param of params) {
        const pair = param.split('=');
        const key = pair[0].toLowerCase();
        if (key === 'filename' || key === 'file' || key === 'name') {
          filename = decodeURIComponent(pair[1] || '');
          break;
        }
      }
    }

    if (!filename) {
      const cleanSrc = src.split('?')[0];
      const parts = cleanSrc.split('/');
      filename = parts[parts.length - 1];
    }

    // Extract file component if name includes path delimiters
    if (filename.includes('/')) {
      const parts = filename.split('/');
      filename = parts[parts.length - 1];
    } else if (filename.includes('\\')) {
      const parts = filename.split('\\');
      filename = parts[parts.length - 1];
    }

    return filename;
  }

  detectFileType(src: string): void {
    if (!src) {
      this.fileType = 'image';
      return;
    }
    
    const filename = this.getFileNameFromUrl(src);
    if (!filename || !filename.includes('.')) {
      this.fileType = 'image';
      return;
    }
    const extParts = filename.split('.');
    const ext = extParts[extParts.length - 1].toLowerCase();

    const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp'];
    const pdfExtensions = ['pdf'];
    const excelExtensions = ['xls', 'xlsx', 'csv', 'ods'];
    const docExtensions = ['doc', 'docx', 'odt'];
    const videoExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'mkv'];

    if (imageExtensions.includes(ext)) {
      this.fileType = 'image';
    } else if (pdfExtensions.includes(ext)) {
      this.fileType = 'pdf';
    } else if (excelExtensions.includes(ext)) {
      this.fileType = 'excel';
    } else if (docExtensions.includes(ext)) {
      this.fileType = 'doc';
    } else if (videoExtensions.includes(ext)) {
      this.fileType = 'video';
    } else {
      this.fileType = 'file';
    }
  }

  getFileName(): string {
    return this.getFileNameFromUrl(this.src) || 'attachment';
  }

  getIconName(): string {
    switch (this.fileType) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'excel':
        return 'table_chart';
      case 'doc':
        return 'description';
      case 'video':
        return 'movie';
      default:
        return 'insert_drive_file';
    }
  }

  downloadFile(): void {
    if (!this.src || this.src.includes('undefined') || this.src.includes('null') || this.src.endsWith('/')) {
      return;
    }
    const filename = this.getFileName();
    if (this.currentObjectUrl) {
      const link = document.createElement('a');
      link.href = this.currentObjectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const fullUrl = this.getFullUrl(this.src);
      const token = this.getEnterpriseToken();
      let headers: { [key: string]: string } = {};
      if (token) {
        headers[Constant.enterPriseKey] = token;
        headers['c-id'] = token;
        headers['x-tenant'] = token;
      }
      this.http.get(fullUrl, { responseType: 'blob', headers: headers, withCredentials: true }).subscribe(
        (blob) => {
          const objUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objUrl);
        },
        (error) => {
          window.open(fullUrl, '_blank');
        }
      );
    }
  }

  loadImage(): void {
    if (!this.src || this.src.includes('undefined') || this.src.includes('null') || this.src.endsWith('/')) {
      this.safeUrl = '/assets/images/logo/no-image.png';
      this.fileType = 'image';
      this.display = true;
      return;
    }

    if (this.isLocalOrDataUrl(this.src)) {
      this.safeUrl = this.src.startsWith('assets/') ? ('/' + this.src) : this.src;
      this.fileType = 'image';
      this.display = true;
      return;
    }

    this.detectFileType(this.src);

    if (this.fileType !== 'image') {
      this.display = true;
      return;
    }

    const fullUrl = this.getFullUrl(this.src);

    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
      this.currentObjectUrl = null;
    }

    const token = this.getEnterpriseToken();
    let headers: { [key: string]: string } = {};
    if (token) {
      headers[Constant.enterPriseKey] = token;
      headers['c-id'] = token;
      headers['x-tenant'] = token;
    }

    this.subscription = this.http.get(fullUrl, {
      responseType: 'blob',
      headers: headers,
      withCredentials: true
    }).subscribe(
      (blob) => {
        this.currentObjectUrl = URL.createObjectURL(blob);
        this.safeUrl = this.sanitizer.bypassSecurityTrustUrl(this.currentObjectUrl);
        this.display = true;
      },
      (error) => {
        this.safeUrl = this.sanitizer.bypassSecurityTrustUrl(fullUrl);
        this.display = true;
      }
    );
  }

  onImageError(event: Event) {
    this.safeUrl = '/assets/images/logo/no-image.png';
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
    }
  }

}

